import { Button, Icon } from '@/components'
import { CSSProperties, ChangeEvent, DragEvent, MouseEvent, useEffect, useRef, useState } from 'react'
import { BaseProps } from '@/components/types'

function AttachFileBox ( props: AttachFileBoxProps ) {
    const {
        style, className, files, single = false,
        singleMaxFileSize, multiMaxFileSize,
        onFileUpload,
        ...rest
    } = props
    const [ value, setValue ] = useState( '' )
    const [ attachFiles, setAttachFiles ] = useState<AttachFiles[]>( files || [] )
    const [ isDrag, setIsDrag ] = useState( false )

    const inputRef = useRef( null )
    const divRef = useRef( null )
    const attachFileBoxStyle: CSSProperties = {
        width: '100%',
        height: '100%',
        display: 'flex -webkit-flex',
        flexDirection: 'column',
        msFlexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: isDrag ? 'rgba(0, 0, 0, 0.1)' : ''
    }

    const getAttachFiles = ( files: FileList ): AttachFiles[] => {
        let valueCheck = true
        let totalFileSize = 0
        const fileList: AttachFiles[] = []

        for ( const file of files ) {
            const { name, size } = file
            if ( !validateMaxFileSize( singleMaxFileSize, size ) ) {
                valueCheck = false
            }
            fileList.push( { name, file } )
            totalFileSize += size
        }

        if ( !validateMaxFileSize( multiMaxFileSize, totalFileSize ) ) {
            valueCheck = false
        }

        return valueCheck ? fileList : []
    }

    const onChange = ( e: ChangeEvent<HTMLInputElement> ): void => {
        const files = e.target?.files

        setAttachFiles( getAttachFiles( files ) )
        setValue( '' )

        onFileUpload && onFileUpload( files )
    }

    const onDragFiles = ( e: DragEvent<HTMLDivElement> ): void => {
        const files = e.dataTransfer?.files

        setAttachFiles( getAttachFiles( files ) )
        setValue( '' )

        onFileUpload && onFileUpload( files )
    }

    const onDragIn = ( e: DragEvent<HTMLDivElement> ) => {
        e.preventDefault()
        e.stopPropagation()

        setIsDrag( true )
    }

    const onDragOut = ( e: DragEvent<HTMLDivElement> ) => {
        e.preventDefault()
        e.stopPropagation()

        setIsDrag( false )
    }
    const onDragOver = ( e: DragEvent<HTMLDivElement> ) => {
        e.preventDefault()
        e.stopPropagation()

        if ( e.dataTransfer?.files ) {
            setIsDrag( true )
        }
    }
    const onDrop = ( e: DragEvent<HTMLDivElement> ) => {
        e.preventDefault()
        e.stopPropagation()

        onDragFiles( e )
        setIsDrag( false )
    }

    const onClickCancel = ( e: MouseEvent<HTMLButtonElement> ) => {
        setAttachFiles( [] )
    }

    const validateFormatFileSize = ( fileSize: string ): boolean => {
        return /.+['M'|'K']$/.test( fileSize )
    }

    const validateMaxFileSize = ( maxFileSize: string, fileSize: number ): boolean => {
        if ( !maxFileSize ) return true
        if ( validateFormatFileSize( maxFileSize ) ) return false
        if ( getMaxFileSize( maxFileSize ) < fileSize ) return false

        return true
    }

    const getMaxFileSize = ( fileSize: string ): number => {
        const size = Number( fileSize.replace( /^[0-9]/g, '' ) )
        const fileSizeUnit = fileSize.replace( /[0-9]/g, '' )

        return fileSizeUnit === 'K' ? 1024 * size : 1024 * 1024 * size
    }


    useEffect ( () => {
        divRef?.current?.addEventListener( 'dragenter', onDragIn )
        divRef?.current?.addEventListener( 'dragleave', onDragOut )
        divRef?.current?.addEventListener( 'dragover', onDragOver )
        divRef?.current?.addEventListener( 'drop', onDrop )

        return () => {
            divRef?.current?.removeEventListener( 'dragenter', onDragIn )
            divRef?.current?.removeEventListener( 'dragleave', onDragOut )
            divRef?.current?.removeEventListener( 'dragover', onDragOver )
            divRef?.current?.removeEventListener( 'drop', onDrop )
        }
    } )

    return (
        <>
            <div
                style={ attachFileBoxStyle }
            >
                <input
                    { ...rest }
                    type="file"
                    multiple={ !single }
                    ref={ inputRef }
                    value={ value }
                    style={ { display: 'none' } }
                    onChange={ onChange }
                />
                {
                    attachFiles.length === 0 ? <>
                        <div
                            ref={ divRef }
                            className="flex flex-column"
                            style={ {
                                width: '100%', height: '100%', textAlign: 'center', cursor: 'pointer',
                                alignItems: 'center', justifyContent: 'center'
                            } }
                            onClick={ () => inputRef.current.click() }
                        >
                            <Icon color="primary">cloud_upload_outlined</Icon>
                            <div>File Upload</div>
                        </div>
                    </> : <>
                        <div
                            className="flex"
                            style={ {
                                height: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                            } }
                        >
                            <div
                                className="flex flex-column"
                                style={ {
                                    width: '100%',
                                    textAlign: 'center',
                                    alignItems: 'center',
                                } }
                            >
                                <Icon color="primary">task_outlined_icon</Icon>
                                <div className="flex flex-column">
                                    { `${ attachFiles[ 0 ]?.name }${ attachFiles.length > 1 ? ` 외 ${attachFiles.length - 1}건` : '' }` }
                                    <Button onClick={ onClickCancel }>
                                        취소
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </>
                }
            </div>

        </>
    )
}
export interface AttachFiles {
    name: string
    file: File
}

export interface AttachFileBoxProps extends BaseProps {
    files?: AttachFiles[]
    single?: boolean
    singleMaxFileSize?: string
    multiMaxFileSize?: string
    onFileUpload?: ( files: FileList ) => void
}

export default AttachFileBox